import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO, addDays } from "date-fns";
import { Search, MapPin } from "lucide-react";
import api from "@/lib/api";
import { DateField } from "@/components/DatePickers";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const TIMES = ["8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM"];
const shortLoc = (loc) => loc.split(",").slice(1, 3).join(",").trim() || loc;

// One borderless "From / Until" segment used inside the desktop pill.
function DateSegment({ label, value, onChange, fromDate, time, onTime, testidDate, testidTime }) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  return (
    <div className="flex flex-col justify-center px-5 py-2 flex-1 min-w-0">
      <span className="text-[11px] font-bold tracking-wide uppercase text-slate-800">{label}</span>
      <div className="flex items-center gap-1.5 mt-0.5">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid={testidDate} className="text-sm text-left outline-none">
              <span className={selected ? "text-slate-900 font-medium" : "text-slate-400"}>{selected ? format(selected, "MMM d") : "Add date"}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={selected} defaultMonth={selected || fromDate} onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd")); setOpen(false); } }} fromDate={fromDate} initialFocus />
          </PopoverContent>
        </Popover>
        <span className="text-slate-300">·</span>
        <select value={time} onChange={(e) => onTime(e.target.value)} data-testid={testidTime} className="text-sm text-slate-500 bg-transparent outline-none cursor-pointer max-w-[74px]">
          {TIMES.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
        </select>
      </div>
    </div>
  );
}

// Turo-style hero search: segmented pill on desktop, stacked card on mobile.
export default function HeroSearch() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loc, setLoc] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [startTime, setStartTime] = useState("10:00 AM");
  const [endTime, setEndTime] = useState("10:00 AM");

  useEffect(() => {
    api.get("/config")
      .then((r) => { const l = r.data?.pickup_locations || []; setLocations(l); if (l.length) setLoc(l[0]); })
      .catch(() => {});
  }, []);

  const search = () => {
    const params = new URLSearchParams();
    if (start && end && end > start) { params.set("start", start); params.set("end", end); params.set("st", startTime); params.set("et", endTime); }
    if (loc === "__near__") params.set("sort", "near");
    else if (loc) params.set("loc", loc);
    const qs = params.toString();
    navigate(`/fleet${qs ? `?${qs}` : ""}`);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Desktop — segmented pill */}
      <div className="hidden md:flex items-stretch bg-white rounded-full p-2 pl-2 shadow-2xl border border-white/60">
        <div className="flex flex-col justify-center px-5 py-2 flex-1 min-w-0">
          <span className="text-[11px] font-bold tracking-wide uppercase text-slate-800 flex items-center gap-1"><MapPin size={11} className="text-blue-600" /> Where</span>
          <select value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="hero-search-where" aria-label="Pickup location" className="text-sm text-slate-700 font-medium bg-transparent outline-none cursor-pointer mt-0.5 -ml-0.5 truncate">
            {locations.length === 0 && <option>Pick-up location</option>}
            <option value="__near__">📍 Near me</option>
            {locations.map((l) => <option key={l} value={l}>{shortLoc(l)}</option>)}
          </select>
        </div>
        <span className="w-px bg-slate-200 my-2.5" />
        <DateSegment label="From" value={start} onChange={setStart} fromDate={new Date()} time={startTime} onTime={setStartTime} testidDate="hero-search-from-date" testidTime="hero-search-from-time" />
        <span className="w-px bg-slate-200 my-2.5" />
        <DateSegment label="Until" value={end} onChange={setEnd} fromDate={start ? addDays(new Date(start), 1) : addDays(new Date(), 1)} time={endTime} onTime={setEndTime} testidDate="hero-search-until-date" testidTime="hero-search-until-time" />
        <button onClick={search} data-testid="hero-search-button" aria-label="Search cars" className="ml-2 self-center shrink-0 grid place-items-center w-14 h-14 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors">
          <Search size={20} />
        </button>
      </div>

      {/* Mobile — stacked card */}
      <div className="md:hidden bg-white rounded-3xl p-4 shadow-2xl text-left">
        <span className="text-[11px] font-bold uppercase text-slate-800 flex items-center gap-1 mb-1.5"><MapPin size={11} className="text-blue-600" /> Where</span>
        <select value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="hero-search-where-m" className="belo-input w-full cursor-pointer">
          {locations.length === 0 && <option>Pick-up location</option>}
          <option value="__near__">📍 Near me</option>
          {locations.map((l) => <option key={l} value={l}>{shortLoc(l)}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <span className="text-xs font-semibold text-slate-500 block mb-1.5">Pickup date</span>
            <DateField value={start} onChange={setStart} fromDate={new Date()} placeholder="Add date" testid="hero-search-from-date-m" />
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-500 block mb-1.5">Pickup time</span>
            <select value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="hero-search-from-time-m" className="belo-input w-full cursor-pointer">
              {TIMES.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
            </select>
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-500 block mb-1.5">Return date</span>
            <DateField value={end} onChange={setEnd} fromDate={start ? addDays(new Date(start), 1) : addDays(new Date(), 1)} placeholder="Add date" testid="hero-search-until-date-m" />
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-500 block mb-1.5">Return time</span>
            <select value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="hero-search-until-time-m" className="belo-input w-full cursor-pointer">
              {TIMES.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
            </select>
          </div>
        </div>
        <button onClick={search} data-testid="hero-search-button-m" className="w-full mt-3 flex items-center justify-center gap-2 bg-blue-600 text-white py-3.5 rounded-full font-semibold hover:bg-blue-700 transition-colors">
          <Search size={18} /> Search cars
        </button>
      </div>
    </div>
  );
}
